import React, { useState, useEffect, lazy, Suspense } from 'react';
import { CartProvider, useCart } from './context/CartContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SiteContentProvider } from './context/SiteContentContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Toast from './components/Toast';
// HomePage is in the main bundle directly — when it was lazy, it caused an extra network round-trip (waterfall)
import HomePage from './pages/HomePage';
// Code-split: each page in its own chunk — first load only brings HomePage code
const ReviewsPage = lazy(() => import('./pages/ReviewsPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const AuthPage = lazy(() => import('./pages/AuthPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const TrackOrderPage = lazy(() => import('./pages/TrackOrderPage'));
const FeedbackPage = lazy(() => import('./pages/FeedbackPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const VideosPage = lazy(() => import('./pages/VideosPage'));
const PolicyPage = lazy(() => import('./pages/PolicyPage'));
import './styles/main.css';
import { setCheckoutFlow } from './utils/checkoutFlow';

// Path <-> page-state maps (mirrors the live site's real URL routing)
const PATH_TO_PAGE = {
  '/': 'home', '/home': 'home', '/reviews': 'reviews', '/contact': 'contact',
  '/blog': 'blog', '/videos': 'videos', '/cart': 'cart', '/checkout': 'checkout',
  '/profile': 'profile', '/track': 'track', '/feedback': 'feedback', '/auth': 'auth',
};
const PAGE_TO_PATH = {
  home: '/', reviews: '/reviews', contact: '/contact', blog: '/blog', videos: '/videos',
  cart: '/cart', checkout: '/checkout', profile: '/profile', track: '/track',
  feedback: '/feedback', auth: '/auth',
};
const PAGE_TITLES = {
  home: 'Organic Slim Fit | Natural Herbal Fat Burn Tea',
  blog: 'Blog | Organic Slim Fit',
  videos: 'Videos | Organic Slim Fit',
  reviews: 'Reviews | Organic Slim Fit',
  contact: 'Contact | Organic Slim Fit',
  cart: 'Cart | Organic Slim Fit',
  checkout: 'Order Now | Organic Slim Fit',
  profile: 'My Profile | Organic Slim Fit',
  track: 'Track Order | Organic Slim Fit',
};

function pathToPage(pathname) {
  if (pathname.startsWith('/blog')) return 'blog';
  if (pathname.startsWith('/pages/')) return `policy:${pathname.replace('/pages/', '')}`;
  return PATH_TO_PAGE[pathname] || 'home';
}

function pageToPath(currentPage) {
  if (currentPage.startsWith('policy:')) return `/pages/${currentPage.split(':')[1]}`;
  return PAGE_TO_PATH[currentPage] || '/';
}

const POLICY_TITLES = {
  'terms-conditions': 'Terms & Conditions',
  'delivery-policy': 'Shipping Policy',
  'refund-policy': 'Refund Policy',
  'return-exchange-policy': 'Return & Exchange Policy',
  'cancellation-policy': 'Cancellation Policy',
  'privacy-policy': 'Privacy Policy',
};

function useRouter() {
  const { currentPage, setCurrentPage } = useCart();
  const [blogSlug, setBlogSlug] = useState(null);
  const [feedbackOrderId, setFeedbackOrderId] = useState('');
  const [feedbackStars, setFeedbackStars] = useState(0);

  // Initial load: read query params + pathname
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fbOrder = params.get('feedback');
    const fbStars = parseInt(params.get('stars') || '0', 10);
    const pathname = window.location.pathname;

    if (fbOrder) {
      setFeedbackOrderId(fbOrder);
      setFeedbackStars(fbStars || 0);
      setCurrentPage('feedback');
      window.history.replaceState({}, '', '/feedback');
      return;
    }
    if (params.get('track')) {
      setCurrentPage('track');
      window.history.replaceState({}, '', '/track');
      return;
    }
    if (pathname.startsWith('/blog/')) {
      const slug = pathname.replace('/blog/', '');
      if (slug) setBlogSlug(slug);
      setCurrentPage('blog');
      return;
    }
    setCurrentPage(pathToPage(pathname));
  }, []);

  // Keep the address bar + document title in sync with currentPage
  useEffect(() => {
    const targetPath = pageToPath(currentPage);
    if (currentPage !== 'blog' && window.location.pathname !== targetPath) {
      window.history.pushState({ page: currentPage }, '', targetPath);
    }
    if (currentPage.startsWith('policy:')) {
      const slug = currentPage.split(':')[1];
      document.title = `${POLICY_TITLES[slug] || 'Policy'} | Organic Slim Fit`;
    } else {
      document.title = PAGE_TITLES[currentPage] || 'Organic Slim Fit';
    }
  }, [currentPage]);

  // Back/forward browser buttons
  useEffect(() => {
    const onPop = () => {
      const pathname = window.location.pathname;
      if (pathname.startsWith('/blog/')) {
        setBlogSlug(pathname.replace('/blog/', ''));
        setCurrentPage('blog');
      } else {
        setCurrentPage(pathToPage(pathname));
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  return { blogSlug, feedbackOrderId, feedbackStars };
}

function AppContent() {
  const { currentPage, setCurrentPage } = useCart();
  const { authLoading } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const { blogSlug, feedbackOrderId, feedbackStars } = useRouter();

  const hideFooter = ['cart', 'checkout'].includes(currentPage);

  // The whole website is NOT blocked on authLoading (it used to show a full-screen 'Loading...'
  // which slowed LCP). Only the Profile page waits for the login state.

  return (
    <>
      <Navbar onAuthClick={() => setShowAuth(true)} onOrderNow={() => { setCheckoutFlow('choose'); setCurrentPage('checkout'); }} />
      <main>
        <Suspense fallback={<div style={{ minHeight: '100vh', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '30vh' }}><p style={{ color: 'var(--mid-green)', fontWeight: 600 }}>Loading... 🍃</p></div>}>
        {currentPage === 'home'     && <HomePage onOrderNow={() => { setCheckoutFlow('choose'); setCurrentPage('checkout'); }} />}
        {currentPage === 'reviews'  && <ReviewsPage />}
        {currentPage === 'contact'  && <ContactPage />}
        {currentPage === 'blog'     && <BlogPage initialSlug={blogSlug} />}
        {currentPage === 'videos'   && <VideosPage />}
        {currentPage === 'cart'     && <CartPage />}
        {currentPage === 'checkout' && <CheckoutPage />}
        {currentPage === 'profile'  && (authLoading ? null : <ProfilePage />)}
        {currentPage === 'track'    && <TrackOrderPage />}
        {currentPage === 'feedback' && (
          <FeedbackPage orderId={feedbackOrderId} preRating={feedbackStars} />
        )}
        {currentPage === 'auth'     && <AuthPage onSuccess={() => setCurrentPage('home')} onClose={() => setCurrentPage('home')} />}
        {currentPage.startsWith('policy:') && (
          <PolicyPage slug={currentPage.split(':')[1]} onBack={() => { setCurrentPage('home'); window.scrollTo({ top: 0 }); }} />
        )}
        </Suspense>
      </main>
      {!hideFooter && <Footer />}
      <Toast />
      {showAuth && (
        <Suspense fallback={null}>
          <AuthPage
            onSuccess={() => { setShowAuth(false); }}
            onClose={() => setShowAuth(false)}
          />
        </Suspense>
      )}
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <SiteContentProvider>
          <AppContent />
        </SiteContentProvider>
      </CartProvider>
    </AuthProvider>
  );
}
